import React from "react";
import { Box } from "@mui/material";

// Cube face size is driven by the --cube-size CSS variable, which switches
// between a small range below the md breakpoint (single-column stacked
// layout) and a larger range at md+ (side-by-side with text).
const SIZE = "var(--cube-size)";
const HALF = "calc(var(--cube-size) / 2)";

// Side faces drive their two band colors through @property-registered
// CSS variables (see <style> block in the component). The keyframes
// animate the variables, and the gradient is rebuilt every frame —
// this guarantees per-channel color interpolation regardless of how
// the browser handles gradient-image animation.
const SIDE_BG =
  "linear-gradient(to bottom, var(--cube-green) 0%, var(--cube-green) 50%, var(--cube-cream) 50%, var(--cube-cream) 100%)";

const BOTTOM_BG =
  "linear-gradient(to bottom, #0f6e63 0%, #0f6e63 33%, #ececec 33%, #ececec 66%, #0f6e63 66%, #0f6e63 100%)";

const faceBaseSx = {
  position: "absolute",
  width: SIZE,
  height: SIZE,
  backfaceVisibility: "visible",
};

const sideFaceSx = (rotateY, delay, duration) => ({
  ...faceBaseSx,
  background: SIDE_BG,
  transform: `rotateY(${rotateY}deg) translateZ(${HALF})`,
  animation: `cubeShade ${duration}s ease-in-out infinite`,
  ...(delay !== undefined && { animationDelay: `${delay}s` }),
});

// Registered globally so the animated custom properties below interpolate
// as colors instead of as opaque strings (which would snap discretely).
const PROPERTY_REGISTRATIONS = `
  @property --cube-green {
    syntax: '<color>';
    inherits: false;
    initial-value: #0f6e63;
  }
  @property --cube-cream {
    syntax: '<color>';
    inherits: false;
    initial-value: #ececec;
  }
`;

const RotatingCubeLogo = ({ size, duration = 18 }) => (
  <>
    <style>{PROPERTY_REGISTRATIONS}</style>
    <Box
      aria-hidden
      sx={{
        // A fixed `size` prop overrides the responsive default. Default:
        // locked at 90px under 1080px, growing 90 -> 190px from 1080 -> 1720px
        // (`15.6vw - 78px` passes through both points), then pinned at 190px.
        ...(size
          ? { "--cube-size": typeof size === "number" ? `${size}px` : size }
          : {
              "--cube-size": "90px",
              "@media (min-width: 1080px)": {
                "--cube-size": "clamp(90px, calc(15.6vw - 78px), 190px)",
              },
            }),

        position: "relative",
        width: "100%",
        aspectRatio: "1 / 1",
        perspective: "1400px",
        perspectiveOrigin: "50% 70%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",

        "@media (prefers-reduced-motion: reduce)": {
          "& *": { animation: "none !important" },
        },

        "@keyframes cubeSpin": {
          from: { transform: "rotateZ(-30deg) rotateX(45deg) rotateY(45deg)" },
          to: { transform: "rotateZ(-30deg) rotateX(45deg) rotateY(405deg)" },
        },
        // Drives the two band colors through registered <color> custom
        // properties. Peak (0%/100%) lifts the green to a punchier #02a676;
        // trough (50%) dims to #083c36. Because the variables are typed,
        // browsers interpolate them per channel each frame — no discrete
        // snap mid-segment. ease-in-out on the animation rounds the peaks
        // and troughs so reversals don't form a sharp V.
        "@keyframes cubeShade": {
          "0%": {
            "--cube-green": "#02a676",
            "--cube-cream": "#ffffff",
          },
          "50%": {
            "--cube-green": "#083c36",
            "--cube-cream": "#828282",
          },
          "100%": {
            "--cube-green": "#02a676",
            "--cube-cream": "#ffffff",
          },
        },

        "&:hover .cube, &:hover .face": {
          animationPlayState: "paused",
        },
      }}
    >
      <Box
        className="cube"
        sx={{
          position: "relative",
          width: SIZE,
          height: SIZE,
          transformStyle: "preserve-3d",
          animation: `cubeSpin ${duration}s linear infinite`,
        }}
      >
        <Box className="face" sx={sideFaceSx(0, undefined, duration)} />
        <Box className="face" sx={sideFaceSx(90, -duration / 4, duration)} />
        <Box className="face" sx={sideFaceSx(180, -duration / 2, duration)} />
        <Box
          className="face"
          sx={sideFaceSx(-90, (-3 * duration) / 4, duration)}
        />
        <Box
          className="face"
          sx={{
            ...faceBaseSx,
            background: "#0a4540",
            transform: `rotateX(90deg) translateZ(${HALF})`,
          }}
        />
        <Box
          className="face"
          sx={{
            ...faceBaseSx,
            background: BOTTOM_BG,
            transform: `rotateX(-90deg) translateZ(${HALF})`,
            filter: "brightness(0.92)",
          }}
        />
      </Box>
    </Box>
  </>
);

export default RotatingCubeLogo;
